import { AspectRatio, Utils } from '@livecontrol/core-ui';
import cx from 'classnames';
import type { DateTime } from 'luxon';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import type { VideoJsPlayer } from 'video.js';
import videojs from 'video.js';

// eslint-disable-next-line import/no-internal-modules
import 'video.js/dist/video-js.css';
/* eslint-disable import/no-unassigned-import */
import 'videojs-http-source-selector';
import 'videojs-contrib-quality-levels';
import { Timer } from './timer';

const StyledContent = styled(AspectRatio)`
  position: relative;
`;

export const Wrapper = (
  props: React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
): React.ReactElement => <StyledContent ratio={16 / 9} {...props} />;

const StyledVideoStatus = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
  border-radius: 5px;
  display: flex;

  .text-success {
    color: #69d7ba;
  }
  .text-danger {
    color: #ff505f;
  }
`;

export const Thumbnail = ({ src }: { src: string }): React.ReactElement => (
  <AspectRatio
    ratio={16 / 9}
    style={{
      borderRadius: 4,
      ...(src
        ? {
            backgroundSize: 'cover',
            backgroundImage: `url('${src}')`,
            boxShadow: 'inset 0px 50px 20px -20px var(--black60)'
          }
        : {})
    }}
  />
);

export const VideoStatus = ({
  status
}: {
  status: 'live' | 'past' | 'upcoming';
}): React.ReactElement | null =>
  status === 'past' ? null : (
    <StyledVideoStatus className='font-size-12px'>
      <i
        className={cx('fas fa-circle mr-8px', {
          'text-success': status === 'upcoming',
          'text-danger': status === 'live'
        })}
      />
      <span className='text-uppercase text-white'>{status}</span>
    </StyledVideoStatus>
  );

const StyledVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  .vjs-control-bar {
    background-color: transparent;
    justify-content: flex-end;
    margin-bottom: 12px;

    .vjs-control.vjs-volume-panel {
      margin-right: 12px;
      > .vjs-button {
        background-color: var(--dark);
      }
    }
  }
`;

export const Video = ({
  src,
  initialVolume = 0,
  onVolumeChange,
  muted,
}: {
  src: string;
  initialVolume?: number;
  onVolumeChange?: (volume: number) => void;
  muted?: boolean
}): React.ReactElement => {
  const ref = useRef<Element>();
  const videoRef = useRef<VideoJsPlayer>();
  const onVolumeChangeRef = useRef(onVolumeChange);

  onVolumeChangeRef.current = onVolumeChange;

  useEffect(() => {
    if (ref.current) {
      videoRef.current = videojs(ref.current, {
        fill: true,
        aspectRatio: '16:9',
        controls: true,
        controlBar: {
          volumePanel: {
            inline: false,
            volumeControl: {
              vertical: true
            }
          },
          children: []
        },
        autoplay: true,
        defaultVolume: initialVolume,
        muted: muted ? muted : initialVolume === 0
      });

      if (initialVolume) {
        videoRef.current.volume(initialVolume);
      }

      videoRef.current.on('volumechange', () => {
        const volume = videoRef.current?.player().volume();

        if (volume) {
          onVolumeChangeRef.current?.(volume);
        }
      });

      videoRef.current.on('pause', () => {
        videoRef.current?.play();
      });
    }

    return (): void => {
      videoRef.current?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledVideo
      controls
      className='video-js vjs-big-play-centered'
      ref={(r): void => {
        ref.current = r as Element;
      }}
    >
      <source src={src} />
    </StyledVideo>
  );
};

const StyledCountdown = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);

  > div {
    padding: var(--spacer-4px);
    border-radius: var(--border-radius-sm);
    background-color: var(--black60);
    box-shadow: 0px 0px 30px var(--black60);
  }
`;

export const Countdown = ({ start }: { start: DateTime }): React.ReactElement | null => {
  const { days, hours, minutes, seconds, complete } = Utils.useCountdown(start);

  return complete ? null : (
    <StyledCountdown>
      <Timer days={days} hours={hours} minutes={minutes} seconds={seconds} />
    </StyledCountdown>
  );
};
